import cn from 'classnames'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

import { NumberInput } from 'cng-common/components/numberInput'
import { useFormField } from 'cng-common/features/form'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { TranslationMode } from 'cng-common/features/translations/configs'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Button } from 'core-components/library/button'
import { Dropdown } from 'core-components/library/dropdown'
import { Input, Textarea } from 'core-components/library/inputs'
import { Tooltip } from 'core-components/library/tooltip'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'

import { MDF_ACTIVITIES_TRANSLATION_KEYS } from '../../configs/translations'
import {
  createMdfOutcomeItem,
  isMdfOutcomeValueType,
  normalizeMdfOutcome,
  normalizeMdfOutcomeItemValue,
  parseMdfOutcome,
  serializeMdfOutcome,
} from '../../lib/mdfOutcome'
import { EMdfOutcomeValueType, IMdfOutcomeItem } from '../../model/mdfOutcomeItem'

import { IMdfOutcomeValueTypeConfig, MDF_OUTCOME_VALUE_TYPES } from './config'

import styles from './MdfActivityOutcomeFields.module.scss'

const KEYS = MDF_ACTIVITIES_TRANSLATION_KEYS.outcomes

interface IMdfOutcomeItemsEditorProps {
  formKey: string
  initialValue?: string | null
  labelClassName?: string
  isDisabled?: boolean
  autoFocus?: boolean
  onChange?: (value: string) => void
}

interface IMdfOutcomeValueTypeOption extends IMdfOutcomeValueTypeConfig {
  label: string
}

interface IMdfOutcomeItemRowProps {
  item: IMdfOutcomeItem
  isDisabled?: boolean
  onChange: (item: IMdfOutcomeItem) => void
  onRemove: () => void
}

const MdfOutcomeItemRow = ({ item, isDisabled, onChange, onRemove }: IMdfOutcomeItemRowProps) => {
  const t = useSafetyTranslation()
  const translationMode = useSelector(selectTranslationMode)
  const isTranslationMode = translationMode === TranslationMode.Edit

  const valueTypeOptions = useMemo<IMdfOutcomeValueTypeOption[]>(
    () => MDF_OUTCOME_VALUE_TYPES.map((config) => ({ ...config, label: t(config.labelKey) })),
    [t]
  )
  const valueTypesSource = useMemo(
    () =>
      new Crud<IMdfOutcomeValueTypeOption>({
        fetcher: new Static<IMdfOutcomeValueTypeOption>(valueTypeOptions),
        keyField: 'value',
        defaultPaging: { page: 0, pageSize: valueTypeOptions.length },
      }),
    [valueTypeOptions]
  )
  const selectedType = valueTypeOptions.find((option) => option.value === item.valueType)

  const handleTypeChange = useCallback(
    (value: string) => {
      if (!isMdfOutcomeValueType(value)) {
        return
      }

      onChange({
        ...item,
        valueType: value,
        value: normalizeMdfOutcomeItemValue(item.value, value),
      })
    },
    [item, onChange]
  )

  const handleNumericValueChange = useCallback(
    (value: string) => {
      onChange({ ...item, value: normalizeMdfOutcomeItemValue(value, item.valueType) })
    },
    [item, onChange]
  )

  const isFreeText = item.valueType === EMdfOutcomeValueType.FreeText
  const isPercentage = item.valueType === EMdfOutcomeValueType.Percentage

  return (
    <div className={styles.outcomeItemRow}>
      <div className={styles.outcomeFieldControl}>
        {isTranslationMode && <Translation text={KEYS.itemNamePlaceholder} />}
        <Input
          type="text"
          value={item.name}
          placeholder={t(KEYS.itemNamePlaceholder)}
          disabled={isDisabled}
          autocompleteOff
          onChange={(name) => onChange({ ...item, name })}
        />
      </div>

      <div className={styles.outcomeFieldControl}>
        {isTranslationMode && selectedType && <Translation text={selectedType.labelKey} />}
        <Dropdown<IMdfOutcomeValueTypeOption>
          disabled={isDisabled}
          disabledInput
          source={valueTypesSource}
          value={item.valueType}
          caption="label"
          maxListPageSize={valueTypeOptions.length}
          startContent={selectedType ? <i className={selectedType.icon} aria-hidden /> : undefined}
          renderFunction={(option) => (
            <span className={styles.valueTypeOption}>
              <i className={option.icon} aria-hidden />
              <span>{option.label}</span>
            </span>
          )}
          onChange={handleTypeChange}
        />
      </div>

      <div className={styles.outcomeFieldControl}>
        {isTranslationMode && <Translation text={KEYS.itemValuePlaceholder} />}
        {isFreeText ? (
          <Textarea
            value={typeof item.value === 'string' ? item.value : ''}
            placeholder={t(KEYS.itemValuePlaceholder)}
            disabled={isDisabled}
            autocompleteOff
            rows={3}
            onChange={(value) => onChange({ ...item, value })}
          />
        ) : (
          <NumberInput
            value={typeof item.value === 'number' ? String(item.value) : ''}
            placeholder={t(KEYS.itemValuePlaceholder)}
            disabled={isDisabled}
            min={0}
            max={isPercentage ? 100 : undefined}
            maximumFractionDigits={item.valueType === EMdfOutcomeValueType.Financial ? 2 : 3}
            endContent={
              isPercentage ? <span className={styles.percentageSuffix}>%</span> : undefined
            }
            onChange={handleNumericValueChange}
          />
        )}
      </div>

      <Tooltip
        className={styles.outcomeActionTooltip}
        tipContent={() => <Translation text={KEYS.removeItem} />}
        inline
      >
        <Button
          className={styles.outcomeRemoveButton}
          variant="outlined"
          color="danger"
          iconButton="kbase kbase-minus"
          disabled={isDisabled}
          onClick={onRemove}
        />
      </Tooltip>
    </div>
  )
}

export const MdfOutcomeItemsEditor = ({
  formKey,
  initialValue,
  labelClassName,
  isDisabled,
  autoFocus,
  onChange,
}: IMdfOutcomeItemsEditorProps) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const translationMode = useSelector(selectTranslationMode)
  const isTranslationMode = translationMode === TranslationMode.Edit
  const { value, handleChange } = useFormField<string>({
    formKey,
    field: 'outcome',
    defaultValue: normalizeMdfOutcome(initialValue),
    isDisabled,
    onChange,
  })
  const outcomeItems = useMemo(() => parseMdfOutcome(value), [value])

  useEffect(() => {
    if (autoFocus && !isDisabled) {
      const control =
        rootRef.current?.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea') ??
        rootRef.current?.querySelector<HTMLButtonElement>('button')
      control?.focus()
    }
  }, [autoFocus, isDisabled])

  const updateOutcomeItems = useCallback(
    (items: IMdfOutcomeItem[]) => handleChange(serializeMdfOutcome(items)),
    [handleChange]
  )
  const handleAddItem = useCallback(
    () => updateOutcomeItems([...outcomeItems, createMdfOutcomeItem()]),
    [outcomeItems, updateOutcomeItems]
  )
  const handleChangeItem = useCallback(
    (index: number, item: IMdfOutcomeItem) =>
      updateOutcomeItems(
        outcomeItems.map((currentItem, currentIndex) =>
          currentIndex === index ? item : currentItem
        )
      ),
    [outcomeItems, updateOutcomeItems]
  )
  const handleRemoveItem = useCallback(
    (index: number) =>
      updateOutcomeItems(outcomeItems.filter((_, currentIndex) => currentIndex !== index)),
    [outcomeItems, updateOutcomeItems]
  )

  return (
    <div ref={rootRef} className={styles.outcomeEditor}>
      <div className={styles.outcomeEditorHeader}>
        <span className={cn(styles.label, labelClassName)}>
          <Translation text={KEYS.otherInfo} span />
        </span>
        <Tooltip
          className={styles.outcomeActionTooltip}
          tipContent={() => <Translation text={KEYS.addItem} />}
          inline
        >
          <Button
            className={styles.outcomeAddButton}
            variant="outlined"
            color="primary"
            iconButton="kbase kbase-add"
            disabled={isDisabled}
            onClick={handleAddItem}
          />
        </Tooltip>
        {isTranslationMode && <Translation text={KEYS.addItem} span />}
        {isTranslationMode && <Translation text={KEYS.removeItem} span />}
      </div>

      {outcomeItems.length > 0 && (
        <>
          <div className={styles.outcomeColumnHeaders}>
            <span>
              <Translation text={KEYS.itemName} span />
            </span>
            <span>
              <Translation text={KEYS.itemValueType} span />
            </span>
            <span>
              <Translation text={KEYS.itemValue} span />
            </span>
            <span aria-hidden />
          </div>
          <div className={styles.outcomeItems}>
            {outcomeItems.map((item, index) => (
              <MdfOutcomeItemRow
                key={index}
                item={item}
                isDisabled={isDisabled}
                onChange={(changedItem) => handleChangeItem(index, changedItem)}
                onRemove={() => handleRemoveItem(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

MdfOutcomeItemsEditor.displayName = 'MdfOutcomeItemsEditor'
